import { useEffect, useState } from 'react';
import { Button, Col, Divider, Layout, Popconfirm, Row, Space, Table, Menu, Input, Tooltip } from 'antd';
import Title from 'antd/lib/typography/Title';
import { useNavigate } from "react-router-dom";
import { ColumnsType } from 'antd/lib/table';
import { AdminService } from '../Services/AdminService';
import { formatDateString, formatToShortFormat } from '../Helpers/DateHelpers';
import { AuthProvider } from '../Providers/authProvider';
import { useAuth } from '../Contexts/AuthContext';
import { getCurrentLanguage } from '../Helpers/RouteHelper';
import { CompanyRole } from '../Types/CompanyRole';
import { SubscriptionLevel } from '../Types/SubscriptionLevel';
import { message } from "antd"
import Page from '../Components/Page/Page';
import { mapSubscriptionLevel } from '../Mappers/mapSubscriptionLevel';
import AdminSubscriptionForm from './AdminSubscriptionForm';
import AdminDashboard from './AdminDashboard';
import AdminIntegrationSettings from './AdminIntegrationSettings';
import { BankOutlined, DashboardOutlined, FileTextOutlined, LockOutlined, SettingOutlined, UserOutlined } from '@ant-design/icons';
import AdminCreateCompanyForm from './AdminCreateCompanyForm';
import AdminInvoiceForm from './AdminInvoiceForm';
import AdminUserEditForm from './AdminUserEditForm';
import AdminCustomerInvoices from './AdminCustomerInvoices';
import AdminPingPaymentReport from './AdminPingPaymentReport';
import AdminLoginAsMemberModal from './AdminLoginAsMemberModal';
import { WebApiAdminDtoCreateUserDto } from '../Repository/eventbookyapi';

// Mirrors AppSettings.MaxLoginFailedCount on the API (WebApi/appsettings.json)
const LOCKOUT_THRESHOLD = 5;

const AdminPage = () => {
    const navigation = useNavigate();
    const [users, setUsers] = useState([] as any);
    const [companyToDeleteMembers, setCompanyToDeleteMembers] = useState(0 as number);
    const [companyToDeleteFields, setCompanyToDeleteFields] = useState(0 as number);
    const [hasFullAccess, setHasFullAccess] = useState(false as boolean);
    const [subscriptionModal, setSubscriptionModal] = useState({ visible: false, companyId: null as null | number, subscriptionLevel: SubscriptionLevel.Free, date: '', smsCredits: 0 });
    const [createCompanyModalVisible, setCreateCompanyModalVisible] = useState(false as boolean);
    const [invoiceModal, setInvoiceModal] = useState({ visible: false, companyId: null as null | number, companyName: '', subscriptionLevel: SubscriptionLevel.Free });
    const [userEditModal, setUserEditModal] = useState({ visible: false, userId: null as null | number, email: '', loginFailedCount: 0 });
    const [loginAsMemberModal, setLoginAsMemberModal] = useState({ visible: false, companyId: null as null | number, companyName: '' });
    const [selectedMenu, setSelectedMenu] = useState('users');
    const [searchText, setSearchText] = useState('');

    useEffect(() => {
        AuthProvider().GetLoggedInUser().then(user => {
            if (user === null) {
                navigation("/" + getCurrentLanguage());
            }
            loadEvents();
        });

    }, []);

    const loadEvents = () => {
        AdminService.GetAllUsers().then(result => {
            const users: ColumnsType<DataType> = result.users as any;
            setHasFullAccess(result.hasFullAccess!);
            setUsers(users);
        }).catch(result => {
            navigation("/" + getCurrentLanguage());
        })
    }

    // Open subscription modal for company
    const onCompanyClick = (user: DataType) => {
        setSubscriptionModal({
            visible: true,
            companyId: user.companyId,
            subscriptionLevel: user.subscriptionLevel,
            date: user.nextSubscriptionDate ? new Date(user.nextSubscriptionDate).toISOString().slice(0, 10) : '',
            smsCredits: (user as any).smsCredits ?? 0,
        });
    };

    const onEditClick = async (userId: number, companyId: number) => {
        const loginResponse = await AdminService.SetUserAsAdmin(userId, companyId);

        if (loginResponse.id != null) {
            AuthProvider().SetUser({
                id: loginResponse.id!,
                firstName: loginResponse.firstName!,
                lastName: loginResponse.lastName!,
                email: loginResponse.email!,
                companyId: loginResponse.companyId!,
                companyLanguage: loginResponse.companyLanguage!,
                subscriptionLevel: loginResponse.subscriptionLevel!,
                hasPaidSubscription: loginResponse.subscriptionLevel! != SubscriptionLevel.Free,
                role: loginResponse.role!,
                paymentConfiguration: loginResponse.paymentConfiguration!,
                companyType: loginResponse.companyType!,
                familyMemberConfiguration: loginResponse.familyMemberConfiguration!,
                familyMemberDisplayMode: loginResponse.familyMemberDisplayMode!,
                hasOnlyReadRights: loginResponse.role === CompanyRole.MemberControlAdmin,
                maxAdministrators: loginResponse.maxAdministrators!,
                maxReadAdministrators: loginResponse.maxReadAdministrators!,
                maxEmails: loginResponse.maxEmails!,
                maxMembers: loginResponse.maxMembers!,
                smsCredits: loginResponse.smsCredits!,
                companyName: loginResponse.companyName!
            });
            navigation("/" + getCurrentLanguage());
        }
    }

    const onUserEditClick = (user: DataType) => {
        setUserEditModal({
            visible: true,
            userId: user.userId,
            email: user.email,
            loginFailedCount: user.loginFailedCount ?? 0,
        });
    };

    const handleUserEditSubmit = async (values: { loginFailedCount: number; newPassword?: string }) => {
        if (!userEditModal.userId) return;
        try {
            if (values.loginFailedCount !== userEditModal.loginFailedCount) {
                await AdminService.SetLoginFailedCount(userEditModal.userId, values.loginFailedCount);
            }
            if (values.newPassword) {
                await AdminService.SetUserPassword(userEditModal.userId, values.newPassword);
            }
            message.success('User updated');
            setUserEditModal({ ...userEditModal, visible: false });
            loadEvents();
        } catch (e) {
            message.error('Failed to update user');
        }
    };

    // Handle subscription update
    const handleSubscriptionSubmit = async (values: { subscriptionLevel: SubscriptionLevel; date: string; smsCredits: number }) => {
        if (!subscriptionModal.companyId) return;
        try {
            await AdminService.SetSubscription(subscriptionModal.companyId, {
                subscriptionLevel: values.subscriptionLevel,
                nextSubscriptionRenewalDate: values.date ? new Date(values.date) : undefined,
                smsCredits: values.smsCredits,
            });
            message.success('Subscription updated');
            setSubscriptionModal({ ...subscriptionModal, visible: false });
            loadEvents();
        } catch (e) {
            message.error('Failed to update subscription');
        }
    };

    const handleDeleteMembersConfirm = async () => {
        await AdminService.DeleteAllCompanyMembers(companyToDeleteMembers);
        setCompanyToDeleteMembers(0);
        loadEvents();
        message.success("Medlemmar är borttagna");
    };

    const handleDeleteFieldsConfirm = async () => {
        await AdminService.DeleteAllMemberFields(companyToDeleteFields);
        setCompanyToDeleteFields(0);
        loadEvents();
        message.success("Medlemsfält och sparad data är borttagna");
    };

    const handleCreateCompanySubmit = async (values: WebApiAdminDtoCreateUserDto) => {
        try {
            await AdminService.CreateCompany(values);
            message.success('Company created');
            setCreateCompanyModalVisible(false);
            loadEvents();
        } catch {
            message.error('Failed to create company');
        }
    };

    interface DataType {
        key: string;
        userId: number;
        email: string;
        fullName: string;
        company: string;
        companyId: number;
        memberCount: string;
        subscriptionLevel: SubscriptionLevel;
        nextSubscriptionDate: Date;
        created: string;
        lastLoginAt: string;
        smsCredits: number;
        loginFailedCount: number;
        siteUrl: string | null;
    }

    interface CompanyDataType {
        key: number;
        companyId: number;
        company: string;
        memberCount: number;
        userCount: number;
        subscriptionLevel: SubscriptionLevel;
        nextSubscriptionDate: Date | null;
        created: string;
        lastLoginAt: string | null;
        siteUrl: string | null;
    }

    const getFilteredUsers = (): DataType[] => {
        if (!searchText) return users;
        const q = searchText.toLowerCase();
        return (users as DataType[]).filter(u =>
            (u.email ?? '').toLowerCase().includes(q) ||
            (u.fullName ?? '').toLowerCase().includes(q) ||
            (u.company ?? '').toLowerCase().includes(q)
        );
    };

    const getFilteredCompanyRows = (): CompanyDataType[] => {
        const all = getCompanyRows();
        if (!searchText) return all;
        const q = searchText.toLowerCase();
        return all.filter(c => (c.company ?? '').toLowerCase().includes(q));
    };

    const getCompanyRows = (): CompanyDataType[] => {
        const map = new Map<number, CompanyDataType>();
        (users as DataType[]).forEach((u) => {
            const existing = map.get(u.companyId);
            if (!existing) {
                map.set(u.companyId, {
                    key: u.companyId,
                    companyId: u.companyId,
                    company: u.company,
                    memberCount: Number(u.memberCount ?? 0),
                    userCount: 1,
                    subscriptionLevel: u.subscriptionLevel,
                    nextSubscriptionDate: u.nextSubscriptionDate ?? null,
                    created: u.created,
                    lastLoginAt: u.lastLoginAt ?? null,
                    siteUrl: u.siteUrl ?? null,
                });
            } else {
                existing.userCount += 1;
                if (u.created && (!existing.created || new Date(u.created) < new Date(existing.created))) {
                    existing.created = u.created;
                }
                if (u.lastLoginAt && (!existing.lastLoginAt || new Date(u.lastLoginAt) > new Date(existing.lastLoginAt))) {
                    existing.lastLoginAt = u.lastLoginAt;
                }
            }
        });
        return Array.from(map.values());
    };

    const companyColumns: ColumnsType<CompanyDataType> = [
        {
            title: 'Company name',
            dataIndex: 'company',
            key: 'company',
            sorter: (a, b) => (a.company ?? '').localeCompare(b.company ?? ''),
            render: (_, row) => (
                <b style={{ cursor: 'pointer', color: '#1890ff' }} onClick={() => onCompanyClick(row as any)}>{row.company}</b>
            ),
        },
        {
            title: 'Members',
            dataIndex: 'memberCount',
            key: 'memberCount',
            sorter: (a, b) => a.memberCount - b.memberCount,
            render: (_, row) => <b>{row.memberCount}</b>,
        },
        {
            title: 'Users',
            dataIndex: 'userCount',
            key: 'userCount',
            sorter: (a, b) => a.userCount - b.userCount,
            render: (_, row) => <b>{row.userCount}</b>,
        },
        {
            title: 'Account level',
            dataIndex: 'subscriptionLevel',
            key: 'subscriptionLevel',
            sorter: (a, b) => (a.subscriptionLevel ?? 0) - (b.subscriptionLevel ?? 0),
            render: (_, row) => <b>{mapSubscriptionLevel(row.subscriptionLevel)}</b>,
        },
        {
            title: 'Next subscription date',
            dataIndex: 'nextSubscriptionDate',
            key: 'nextSubscriptionDate',
            sorter: (a, b) => new Date(a.nextSubscriptionDate ?? 0).getTime() - new Date(b.nextSubscriptionDate ?? 0).getTime(),
            render: (_, row) => <b>{row.nextSubscriptionDate ? formatToShortFormat(row.nextSubscriptionDate) : '–'}</b>,
        },
        {
            title: 'Created',
            dataIndex: 'created',
            key: 'created',
            sorter: (a, b) => new Date(a.created ?? 0).getTime() - new Date(b.created ?? 0).getTime(),
            render: (_, row) => <b>{formatDateString(row.created)}</b>,
        },
        {
            title: 'Last login',
            dataIndex: 'lastLoginAt',
            key: 'lastLoginAt',
            sorter: (a, b) => new Date(a.lastLoginAt ?? 0).getTime() - new Date(b.lastLoginAt ?? 0).getTime(),
            render: (_, row) => <b>{row.lastLoginAt ? formatDateString(row.lastLoginAt) : '–'}</b>,
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, row) => (
                <Space size="middle">
                    <Popconfirm
                        title={"Är du säker, alla medlemmar kommer tas bort för företaget (" + row.company + ")?"}
                        key={"delete-members-company-" + row.companyId}
                        open={row.companyId === companyToDeleteMembers}
                        placement="topRight"
                        onConfirm={handleDeleteMembersConfirm}
                        onCancel={() => setCompanyToDeleteMembers(0)}
                    />
                    {hasFullAccess ? <Button danger onClick={() => setCompanyToDeleteMembers(row.companyId)}>Delete members</Button> : ""}
                    <Button onClick={() => setInvoiceModal({ visible: true, companyId: row.companyId, companyName: row.company, subscriptionLevel: row.subscriptionLevel })}>Generate invoice</Button>
                    <Tooltip title={row.siteUrl ? '' : 'The company has no public site'}>
                        <Button disabled={!row.siteUrl} onClick={() => setLoginAsMemberModal({ visible: true, companyId: row.companyId, companyName: row.company })}>Log in as member</Button>
                    </Tooltip>
                    <Popconfirm
                        title="Är du säker, alla medlemsfält, fältordning och sparad data för specialfält kommer tas bort för företaget?"
                        key={"delete-fields-company-" + row.companyId}
                        open={row.companyId === companyToDeleteFields}
                        placement="topRight"
                        onConfirm={handleDeleteFieldsConfirm}
                        onCancel={() => setCompanyToDeleteFields(0)}
                    />
                    {hasFullAccess ? <Button danger onClick={() => setCompanyToDeleteFields(row.companyId)}>Delete member fields</Button> : ""}
                </Space>
            ),
        },
    ];


    const columns: ColumnsType<DataType> = [
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            sorter: (a, b) => (a.email ?? '').localeCompare(b.email ?? ''),
            render: (_, user) => (
                <b>{user.email}</b>
            ),
        },
        {
            title: 'Company name',
            dataIndex: 'company',
            key: 'company',
            sorter: (a, b) => (a.company ?? '').localeCompare(b.company ?? ''),
            render: (_, user) => (
                <b style={{ cursor: 'pointer', color: '#1890ff' }} onClick={() => onCompanyClick(user)}>{user.company}</b>
            ),
        },
        {
            title: 'Fullname',
            dataIndex: 'fullname',
            key: 'fullname',
            sorter: (a, b) => (a.fullName ?? '').localeCompare(b.fullName ?? ''),
            render: (_, user) => (
                <b>{user.fullName}</b>
            ),
        },
        {
            title: 'Membercount',
            dataIndex: 'membercount',
            key: 'membercount',
            sorter: (a, b) => Number(a.memberCount ?? 0) - Number(b.memberCount ?? 0),
            render: (_, user) => (
                <b>{user.memberCount}</b>
            ),
        },
        {
            title: 'Accountlevel',
            dataIndex: 'subscriptionLevel',
            key: 'subscriptionLevel',
            sorter: (a, b) => (a.subscriptionLevel ?? 0) - (b.subscriptionLevel ?? 0),
            render: (_, user) => (
                <b>{mapSubscriptionLevel(user.subscriptionLevel)}</b>
            ),
        },
        {
            title: 'Next subscription date',
            dataIndex: 'nextSubscriptionDate',
            key: 'nextSubscriptionDate',
            sorter: (a, b) => new Date(a.nextSubscriptionDate ?? 0).getTime() - new Date(b.nextSubscriptionDate ?? 0).getTime(),
            render: (_, user) => (
                <b>{formatToShortFormat(user.nextSubscriptionDate)}</b>
            ),
        },


        {
            title: 'Created',
            dataIndex: 'created',
            key: 'created',
            sorter: (a, b) => new Date(a.created ?? 0).getTime() - new Date(b.created ?? 0).getTime(),
            render: (_, user) => (
                <b>{formatDateString(user.created)}</b>
            ),
        },
        {
            title: 'Last login',
            dataIndex: 'lastLoginAt',
            key: 'lastLoginAt',
            sorter: (a, b) => new Date(a.lastLoginAt ?? 0).getTime() - new Date(b.lastLoginAt ?? 0).getTime(),
            render: (_, user) => (
                <b>{user.lastLoginAt ? formatDateString(user.lastLoginAt) : '–'}</b>
            ),
        },
        {
            title: 'Failed logins',
            dataIndex: 'loginFailedCount',
            key: 'loginFailedCount',
            sorter: (a, b) => Number(a.loginFailedCount ?? 0) - Number(b.loginFailedCount ?? 0),
            render: (_, user) => {
                const isLocked = (user.loginFailedCount ?? 0) >= LOCKOUT_THRESHOLD;
                return (
                    <Space size={6}>
                        {isLocked && <LockOutlined style={{ color: '#cf1322' }} title="Account locked" />}
                        <b style={{ color: isLocked ? '#cf1322' : undefined }}>{user.loginFailedCount ?? 0}</b>
                    </Space>
                );
            },
        },
        {
            title: 'Action',
            key: 'action',
            render: (_, user) => (
                <Space size="middle">
                    <Button onClick={() => onEditClick(user.userId, user.companyId)} type="primary">Open</Button>
                    <Button onClick={() => onUserEditClick(user)}>Manage</Button>
                    <Popconfirm
                        title={"Är du säker, alla medlemmar kommer tas bort för företaget (" + user.company + ")?"}
                        key={"delete-members-" + user.userId}
                        open={user.companyId === companyToDeleteMembers}
                        placement="topRight"
                        onConfirm={handleDeleteMembersConfirm}
                        onCancel={() => setCompanyToDeleteMembers(0)}
                    />
                    {hasFullAccess ? <Button danger onClick={() => setCompanyToDeleteMembers(user.companyId)}>Delete members</Button> : ""}

                    <Popconfirm
                        title="Är du säker, alla medlemsfält, fältordning och sparad data för specialfält kommer tas bort för företaget?"
                        key={"delete-fields-" + user.userId}
                        open={user.companyId === companyToDeleteFields}
                        placement="topRight"
                        onConfirm={handleDeleteFieldsConfirm}
                        onCancel={() => setCompanyToDeleteFields(0)}
                    />
                    {hasFullAccess ? <Button danger onClick={() => setCompanyToDeleteFields(user.companyId)}>Delete member fields</Button> : ""}
                </Space>
            ),
        },
    ];

    const menuItems = [
        {
            key: 'dashboard',
            icon: <DashboardOutlined />,
            label: 'Dashboard',
        },
        {
            key: 'users',
            icon: <UserOutlined />,
            label: 'User Management',
        },
        {
            key: 'companies',
            icon: <BankOutlined />,
            label: 'Company Management',
        },
        {
            key: 'integrationSettings',
            icon: <SettingOutlined />,
            label: 'Integration Settings',
        },
        {
            key: 'customerInvoices',
            icon: <FileTextOutlined />,
            label: 'Customer Invoices',
        },
        {
            key: 'pingPaymentReport',
            icon: <BankOutlined />,
            label: 'Ping Payment Report',
        },
    ];

    return (
        <Page>
            <Row justify="center" align="top">
                <Col span={16} className='bookyBox'>
                    <Title>OneFront 360 Admin Page</Title>
                    <Menu
                        mode="horizontal"
                        selectedKeys={[selectedMenu]}
                        items={menuItems}
                        onClick={(e) => setSelectedMenu(e.key)}
                        style={{ marginBottom: '20px' }}
                    />
                    <br />
                    {selectedMenu !== 'dashboard' && selectedMenu !== 'integrationSettings' && selectedMenu !== 'customerInvoices' && selectedMenu !== 'pingPaymentReport' && (
                        <Input.Search
                            placeholder="Search by name, email or company..."
                            allowClear
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                            style={{ marginBottom: 16, maxWidth: 400 }}
                        />
                    )}
                    {selectedMenu === 'dashboard' ? (
                        <AdminDashboard />
                    ) : selectedMenu === 'integrationSettings' ? (
                        <AdminIntegrationSettings />
                    ) : selectedMenu === 'customerInvoices' ? (
                        <AdminCustomerInvoices />
                    ) : selectedMenu === 'pingPaymentReport' ? (
                        <AdminPingPaymentReport />
                    ) : selectedMenu === 'companies' ? (
                        <>
                            <div style={{ marginBottom: '12px', position: 'relative', zIndex: 1 }}>
                                <Button type="primary" onClick={() => setCreateCompanyModalVisible(true)}>Create company</Button>
                            </div>
                            <Table columns={companyColumns} className="admin-table" style={{ marginTop: 0 }} rowKey="companyId" dataSource={getFilteredCompanyRows()} scroll={{ x: 'max-content' }} />
                        </>
                    ) : (
                        <Table columns={columns} className="admin-table" style={{ marginTop: 0 }} rowKey="Id" dataSource={getFilteredUsers()} scroll={{ x: 'max-content' }} />
                    )}
                </Col>
            </Row>
            <AdminSubscriptionForm
                visible={subscriptionModal.visible}
                onCancel={() => setSubscriptionModal({ ...subscriptionModal, visible: false })}
                onSubmit={handleSubscriptionSubmit}
                initialLevel={subscriptionModal.subscriptionLevel}
                initialDate={subscriptionModal.date}
                initialSmsCredits={subscriptionModal.smsCredits}
            />
            <AdminCreateCompanyForm
                visible={createCompanyModalVisible}
                onCancel={() => setCreateCompanyModalVisible(false)}
                onSubmit={handleCreateCompanySubmit}
            />
            <AdminInvoiceForm
                visible={invoiceModal.visible}
                companyId={invoiceModal.companyId}
                companyName={invoiceModal.companyName}
                subscriptionLevel={invoiceModal.subscriptionLevel}
                onCancel={() => setInvoiceModal({ ...invoiceModal, visible: false })}
            />
            <AdminUserEditForm
                visible={userEditModal.visible}
                onCancel={() => setUserEditModal({ ...userEditModal, visible: false })}
                onSubmit={handleUserEditSubmit}
                userEmail={userEditModal.email}
                initialLoginFailedCount={userEditModal.loginFailedCount}
            />
            <AdminLoginAsMemberModal
                visible={loginAsMemberModal.visible}
                companyId={loginAsMemberModal.companyId}
                companyName={loginAsMemberModal.companyName}
                onCancel={() => setLoginAsMemberModal({ ...loginAsMemberModal, visible: false })}
            />
        </Page>
    );
};

export default AdminPage;